import { SupportProvider } from 'src/modules/support/contexts/support-provider';
import { DesktopSidebar } from 'components/molecules/sidebar/desktop-sidebar';
import { MobileHeader } from 'components/molecules/sidebar/mobile-header';
import { Outlet, useMatches } from '@tanstack/react-router';
import DrawerPhone from 'src/components/organisms/phones/drawer-phone/drawer-phone';
import { useState } from 'react';
import { useUserPhoneNumber } from 'hooks/phones/phones';
import InfoError from 'src/components/atoms/info-error/info-error';
import Phone from 'src/components/organisms/phones/phone/phone';
import { Typography } from '@material-tailwind/react';
import { useSetErrorReporterUserContext } from 'hooks/errors';
import { Route as RootRoute } from 'src/routes/__root';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { FloatingPhone } from 'src/modules/shared/components/organisms/phones/floating-phone';
import Loading from 'src/modules/shared/components/atoms/loading';
import { SupportModeControls } from 'src/modules/shared/components/organisms/support-mode/support-mode-controls';

import { AppNoticesProvider } from 'src/modules/shared/contexts/notices/app-notices-provider';
import { AppNoticeRegion } from 'src/modules/shared/components/organisms/app-notice-region';
import { FrontendUpdateNotice } from 'src/modules/shared/components/organisms/frontend-update-notice';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { useVisibleViewport } from 'src/modules/shared/hooks/use-visible-viewport';
import { useViewportHeightBoundary } from 'src/modules/shared/hooks/use-viewport-height-boundary';
import { ViewportHeightContext } from 'src/modules/shared/contexts/viewport-height-context';

function AuthenticatedApp() {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);
  const viewportMediaQuery = useMatches({
    select: (matches) =>
      matches.find((match) => match.staticData.visualViewportMediaQuery)
        ?.staticData.visualViewportMediaQuery ?? 'not all',
  });
  const fitViewport = useMediaQuery(viewportMediaQuery);
  const { minimumHeight, requestHeight } = useViewportHeightBoundary();
  const viewport = useVisibleViewport(fitViewport, minimumHeight);

  const { phoneNumber, error, loading: phoneLoading } = useUserPhoneNumber();
  const { phoneVersion = 'default' } = RootRoute.useSearch();
  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();

  useSetErrorReporterUserContext();

  const renderDrawerPhone = () => {
    const phoneComponent = () => {
      if (error) {
        return <InfoError error={error} />;
      }
      if (providerLoading || phoneLoading || !callProvider) {
        return <Loading />;
      }
      return <Phone callProvider={callProvider} />;
    };
    const headerComponent = () => {
      return (
        <>
          {/* TODO: check when implementing i18n */}
          <Typography className="text-blue-50 text-xs">Mi número</Typography>
          <Typography className="text-blue-50 text-sm uppercase">
            {phoneNumber}
          </Typography>
        </>
      );
    };

    return (
      <DrawerPhone
        isOpen={isDrawerOpen}
        toggle={setIsDrawerOpen}
        phoneComponent={phoneComponent()}
        headerComponent={headerComponent()}
      />
    );
  };

  const renderFloatingPhone = () => {
    if (providerLoading || !callProvider) return null;

    return <FloatingPhone callProvider={callProvider} />;
  };

  const content = (
    <SupportProvider>
      <AppNoticesProvider>
        <FrontendUpdateNotice />
        <main
          className="flex h-screen w-full flex-col overflow-hidden"
          style={
            viewport
              ? {
                  position: 'fixed',
                  top: viewport.offsetTop,
                  height: viewport.height,
                }
              : undefined
          }
        >
          <MobileHeader />

          <section
            className={`flex flex-1 bg-neutral-50 ${viewport ? 'min-h-0' : 'h-[calc(100vh-64px)] md:h-full md:min-h-0'}`}
          >
            <aside className="hidden md:block flex-none w-auto h-full">
              <DesktopSidebar />
            </aside>

            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <AppNoticeRegion />
              <div
                className={`min-h-0 flex-1 ${viewport ? 'overflow-clip' : 'overflow-auto'}`}
              >
                <SupportModeControls />
                <Outlet />
              </div>
            </div>
          </section>

          {phoneVersion === 'default' && renderDrawerPhone()}
          {phoneVersion === 'v1' && renderFloatingPhone()}
        </main>
      </AppNoticesProvider>
    </SupportProvider>
  );
  return (
    <ViewportHeightContext.Provider
      value={fitViewport ? requestHeight : undefined}
    >
      {content}
    </ViewportHeightContext.Provider>
  );
}

export default AuthenticatedApp;
